import { MoneyValue } from './MoneyValue';
import { formatDateTime, formatDate, number } from '@/lib/format';
import { tenderLabel } from '@/lib/tenderLabel';
import { Separator } from '@/components/ui/separator';
import type { SaleDetail } from '@/types/sale';

type ReceiptItem = NonNullable<SaleDetail['items']>[number];

interface ReceiptPreviewProps {
  detail: SaleDetail;
  /**
   * #2100: a partner's Booking order — the price is the partner's cost, not the
   * guest's (ABT-S22 AC9), so the lines show what is in the order, without money.
   */
  hidePrices?: boolean;
}

type ReceiptAddOn = NonNullable<ReceiptItem['addOns']>[number];

/** One printed line: a product with its quantity and unit price. */
interface ReceiptLine {
  name: string;
  quantity: number;
  unit: number;
  /** A bundle's included contents — printed under it, never flattened. */
  included: ReceiptAddOn[];
}

/**
 * The receipt's lines: entry tickets and attraction tickets side by side.
 *
 * A PAID add-on (an attraction ticket bought with an entry ticket) is
 * flattened out to its own line, the same as the entry ticket — it is a
 * separate purchase with its own price. An INCLUDED add-on is part of a
 * bundle's price, so it stays under its bundle.
 *
 * Identical lines then collapse into one with a summed quantity — same name
 * AND same unit price (e.g. 3× "Virtual Reality" at 35.000 from per-unit
 * add-on slots → one "3 x 35.000" line). A different unit price (slot-based
 * pricing) stays separate, and a bundle with included contents is never merged
 * so each keeps its own. Order is first appearance.
 */
function receiptLines(items: ReceiptItem[]): ReceiptLine[] {
  const flat: ReceiptLine[] = [];
  for (const it of items) {
    const addOns = it.addOns ?? [];
    flat.push({
      name: it.productName ?? '',
      quantity: it.quantity,
      unit: it.price ?? 0,
      included: addOns.filter((a) => a.included),
    });
    for (const a of addOns) {
      if (!a.included) {
        flat.push({ name: a.productName ?? '', quantity: a.quantity, unit: a.price, included: [] });
      }
    }
  }
  const merged: ReceiptLine[] = [];
  const indexByKey = new Map<string, number>();
  for (const line of flat) {
    if (line.included.length > 0) {
      merged.push(line);
      continue;
    }
    const key = `${line.name}|${line.unit}`;
    const at = indexByKey.get(key);
    if (at == null) {
      indexByKey.set(key, merged.length);
      merged.push({ ...line });
    } else {
      merged[at] = { ...merged[at]!, quantity: merged[at]!.quantity + line.quantity };
    }
  }
  return merged;
}

/**
 * One label/value line on the receipt.
 *
 * `justify-between` alone was not enough. A value long enough to wrap — a
 * tender named "Kartu Debit/Kredit (EDC Permata)", or simply a customer with a
 * long name — grew until it met the label, and with no gap between them the
 * two ran together as "PembayaranKartu Debit/Kredit (EDC". So the label never
 * shrinks, there is always a gap, and a wrapped value stays right-aligned
 * under itself instead of drifting back toward the label.
 */
function MetaRow({
  label,
  value,
  numeric = false,
}: {
  label: string;
  value: string;
  numeric?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-ink-400">{label}</dt>
      <dd className={`min-w-0 text-right${numeric ? ' tnum' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * The inclusive tax note — MoM 16/10, "untuk struk tiket tambahkan PBJT".
 *
 * NOT an addend. Park prices contain the tax, so this figure is already inside
 * TOTAL: `Subtotal + tax` deliberately does NOT equal TOTAL, which is why the
 * note sits BELOW the total and is worded "Termasuk …". (The invoice PDF
 * printed this row above "Subtotal" once and it read as something the total was
 * still missing.)
 *
 * The word is the server's — PPN for an older sale or a mixed cart with
 * add-ons, PBJT for an entry-tickets-only sale once the park's ticket tax is
 * on — so this surface cannot invent a label the documents disagree with. The
 * rate is the one THIS sale was sold under; `id-ID` writes a decimal with a
 * comma ("10,5%"), and an un-derivable rate prints the bare type.
 */
function taxNote(tax: NonNullable<SaleDetail['tax']>): string {
  const pct = typeof tax.percent === 'number' && tax.percent > 0 ? tax.percent : null;
  return pct == null
    ? `Termasuk ${tax.type}`
    : `Termasuk ${tax.type} ${String(pct).replace('.', ',')}%`;
}

function paymentLabel(detail: SaleDetail): string | null {
  // The resolved method name from the DB wins; the gateway map is the fallback
  // and is shared with the shift summary (`@/lib/tenderLabel`) so the two
  // surfaces cannot drift apart on what a tender is called.
  if (detail.paymentMethodName) return detail.paymentMethodName;
  return tenderLabel(detail.paymentGateway);
}

/** On-screen receipt mock (Poppins brand) mirroring the printed collateral. */
export function ReceiptPreview({ detail, hidePrices = false }: ReceiptPreviewProps) {
  const method = paymentLabel(detail);
  return (
    <div className="mx-auto w-full max-w-xs rounded-lg border border-dashed border-ink-400/40 bg-white p-5 font-brand text-ink-900 shadow-sm">
      <div className="text-center">
        <img
          src="/logo-autopia-red.png"
          alt="Autopia Museum & Parks"
          className="mx-auto h-9 w-auto object-contain"
        />
        <p className="mt-2 font-display text-sm font-bold text-ink-900">
          Autopia Museum &amp; Parks
        </p>
        <p className="mt-0.5 text-[10px] leading-snug text-ink-400">
          Jl. Raya Lembang No.272, Lembang,
          <br />
          Bandung Barat, Jawa Barat
          <br />
          Indonesia 40391
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-widest text-ink-400">
          {hidePrices ? 'Pesanan Partner' : 'Bukti Pembayaran'}
        </p>
        <p className="tnum mt-1 font-display text-[11px] font-bold text-ink-900">{detail.invoiceId}</p>
      </div>
      <Separator className="my-3 border-dashed" />
      <dl className="space-y-1 text-xs">
        <MetaRow label="Tanggal" value={formatDateTime(detail.createdAt, 'id', { shortMonth: true })} numeric />
        {detail.visitDate && (
          <MetaRow label="Kunjungan" value={formatDate(detail.visitDate, { shortMonth: true })} numeric />
        )}
        <MetaRow
          label="Pelanggan"
          value={detail.userInfo?.name || detail.userInfo?.phone || '—'}
        />
        {hidePrices ? (
          detail.partnerName && <MetaRow label="Partner" value={detail.partnerName} />
        ) : (
          <>
            <MetaRow label="Kasir" value={detail.cashierName || '—'} />
            {method && <MetaRow label="Pembayaran" value={method} />}
          </>
        )}
      </dl>
      <Separator className="my-3 border-dashed" />
      {/*
        Each line is the product name, then — on a priced receipt — a
        "qty x unit price ........ line total" row, without "Rp". Entry and
        attraction tickets are flat, one after another (see receiptLines):
          Tiket Masuk Weekday
          3 x 50.000                 150.000
          Tiket F1
          2 x 50.000                 100.000
        The name gets its own line so it can wrap on the narrow 58mm roll
        instead of being truncated.
      */}
      <ul className="space-y-1.5 text-xs">
        {receiptLines(detail.items ?? []).map((line, i) => (
          <li key={i}>
            {hidePrices ? (
              <div className="flex justify-between gap-2">
                <span className="min-w-0 break-words">{line.name}</span>
                <span className="tnum shrink-0 text-ink-400">×{line.quantity}</span>
              </div>
            ) : (
              <>
                <p className="break-words">{line.name}</p>
                <div className="tnum flex justify-between gap-2">
                  <span className="min-w-0">
                    {line.quantity} x {number(line.unit)}
                  </span>
                  <span className="shrink-0 text-right">{number(line.unit * line.quantity)}</span>
                </div>
              </>
            )}
            {line.included.map((a, j) => (
              <div key={j} className="receipt-minor flex justify-between gap-2 pl-3 text-[11px] text-ink-400">
                <span className="min-w-0 break-words">+ {a.productName}</span>
                <span className="shrink-0 italic">
                  Termasuk
                  {!hidePrices && typeof a.bundleUnitPrice === 'number' && (
                    <span className="tnum"> · {number(a.bundleUnitPrice * a.quantity)}</span>
                  )}
                </span>
              </div>
            ))}
          </li>
        ))}
      </ul>
      {!hidePrices && <Separator className="my-3 border-dashed" />}
      {!hidePrices && (detail.discountAmount ?? 0) > 0 && (
        <dl className="mb-1 space-y-0.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-ink-400">Subtotal</dt>
            <dd className="tnum">
              <MoneyValue value={detail.originalAmount ?? detail.totalAmount} className="text-xs" />
            </dd>
          </div>
          {(detail.discounts?.length ?? 0) > 0 ? (
            detail.discounts!.map((d, i) => (
              <div key={i} className="flex justify-between gap-2 text-success">
                <dt className="min-w-0 truncate">
                  {d.name}
                  {d.couponCode && (
                    <span className="text-ink-400"> ({d.couponCode})</span>
                  )}
                </dt>
                <dd className="tnum shrink-0">
                  − <MoneyValue value={d.amount} className="text-xs text-success" />
                </dd>
              </div>
            ))
          ) : (
            <div className="flex justify-between text-success">
              <dt>Diskon</dt>
              <dd className="tnum">
                − <MoneyValue value={detail.discountAmount} className="text-xs text-success" />
              </dd>
            </div>
          )}
        </dl>
      )}
      {!hidePrices && (
        <div className="flex items-center justify-between">
          <span className="font-display text-sm font-bold uppercase">Total</span>
          <MoneyValue value={detail.totalAmount} emphasis className="text-lg" />
        </div>
      )}
      {!hidePrices && detail.tax && detail.tax.amount > 0 && (
        <div className="receipt-minor mt-1 flex justify-between gap-2 text-[11px] text-ink-400">
          <span className="min-w-0 truncate">{taxNote(detail.tax)}</span>
          <MoneyValue value={detail.tax.amount} className="receipt-minor text-[11px] text-ink-400" />
        </div>
      )}
      {!hidePrices && detail.cash && (
        <dl className="mt-1 space-y-0.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-ink-400">Tunai</dt>
            <dd className="tnum"><MoneyValue value={detail.cash.amountTendered} className="text-xs" /></dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-400">Kembali</dt>
            <dd className="tnum"><MoneyValue value={detail.cash.changeDue} className="text-xs" /></dd>
          </div>
        </dl>
      )}
      <p className="mt-4 text-center text-[10px] text-ink-400">Terima kasih atas kunjungan Anda.</p>
    </div>
  );
}
